import { useCallback } from "react"
import { useSearchParams } from "react-router"

export type SearchParamValue = string | string[] | boolean | null | undefined

export interface UseSearchParamValuesResult<T extends string> {
  searchParams: URLSearchParams
  setValue: (key: T, value: SearchParamValue) => void
  reset: () => void
}

export function useSearchParamValues<T extends string>(
  useCommaSepArrays?: boolean,
): UseSearchParamValuesResult<T> {
  const [searchParams, setSearchParams] = useSearchParams()

  const reset = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  const setValue = useCallback(
    (key: T, value: string | string[] | boolean | null | undefined) => {
      const newParams = new URLSearchParams(searchParams)
      if (value == null || (Array.isArray(value) && value.length === 0)) {
        newParams.delete(key)
      } else if (Array.isArray(value)) {
        if (useCommaSepArrays) {
          // TODO(mgraczyk): Use ordinary urlparam array instead of comma-separated string.
          newParams.set(key, value.join(","))
        } else {
          newParams.delete(key)
          for (const v of value) {
            newParams.append(key, v)
          }
        }
      } else if (typeof value === "boolean") {
        newParams.set(key, value.toString())
      } else {
        newParams.set(key, value)
      }
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams, useCommaSepArrays],
  )

  return { searchParams, reset, setValue }
}
